<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center" style="gap: 16px;">
              <div class="header-icon-wrap">
                <i class="fe fe-credit-card" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Capitation Payments</h4>
                <small style="opacity: .8;">{{ payments.total || 0 }} payment(s) on record</small>
              </div>
            </div>
            <button type="button" class="btn btn-outline-light btn-sm" @click="getPaymentAgency(currentPage ? currentPage : 1)">
              <i class="fe fe-refresh-cw mr-1"></i> Refresh
            </button>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── Filter Card ── -->
        <div class="card table-card shadow mb-4">
          <div class="form-card-header">
            <div class="section-number"><i class="fe fe-sliders" style="font-size:13px;"></i></div>
            <div>
              <p class="mb-0 text-white font-weight-bold" style="font-size:.95rem;">Filter Parameters</p>
              <small style="color:rgba(255,255,255,.7);">Narrow payments by status, type, enrollee and date</small>
            </div>
          </div>
          <div class="card-body">
            <div class="row">

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Select Status</label>
                <v-select v-model="form.status" :options="statuses" />
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Select Payment Type</label>
                <v-select v-model="form.type" :options="types" />
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1"><i class="fe fe-hash mr-1"></i>Enrollee ID</label>
                <input type="text" class="form-control" v-model="form.id_card_number" placeholder="Enrollee ID" />
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Rows per Page</label>
                <select class="form-control" v-model="form.paginate_value">
                  <option>30</option>
                  <option>100</option>
                  <option>500</option>
                  <option>1000</option>
                  <option>5000</option>
                  <option>10000</option>
                  <option>15000</option>
                </select>
              </div>

              <div class="form-group col-md-6">
                <label class="info-label d-block mb-1"><i class="fe fe-calendar mr-1"></i>Start Date</label>
                <input type="date" class="form-control" v-model="form.from" />
              </div>

              <div class="form-group col-md-6">
                <label class="info-label d-block mb-1"><i class="fe fe-calendar mr-1"></i>End Date</label>
                <input type="date" class="form-control" v-model="form.to" />
              </div>

              <div class="col-md-12 pt-3 border-top">
                <div class="d-flex flex-wrap" style="gap:10px;">
                  <button @click="getPaymentAgency(1)" class="btn btn-submit px-4">
                    <i class="fe fe-filter mr-1"></i> Filter
                  </button>
                  <button class="btn btn-outline-dark">
                    <download-excel :data="payments.data" :fields="json_fields" type="xls" :escapeCsv="false"
                      name="payments.xls">
                      <i class="fe fe-file-text mr-1"></i> Export Excel
                    </download-excel>
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- ── Payments Table ── -->
        <div class="card table-card shadow mb-4">
          <div class="d-flex align-items-center px-4 pt-4 pb-3">
            <i class="fe fe-list text-primary mr-2"></i>
            <h6 class="mb-0 font-weight-bold">Payments</h6>
          </div>
          <div class="table-responsive">
            <table class="table align-td-middle mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">Date</th>
                  <th class="table-th">Amount</th>
                  <th class="table-th">Enrollee</th>
                  <th class="table-th">Status</th>
                  <th class="table-th">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr class="table-row" v-for="payment in payments.data" v-bind:key="payment.id">
                  <td class="py-2" style="font-size:.85rem;">{{ payment.created_at }}</td>
                  <td class="py-2 font-weight-bold">
                    <i class="mdi mdi-currency-ngn"></i> {{ payment.amount | numeral(0, 0) }}
                  </td>
                  <td class="py-2">
                    <span v-if="payment.client != null">{{ payment.client.full_name }}</span>
                  </td>
                  <td class="py-2">
                    <span :class="`badge badge-soft-${payment.color}`" style="font-size:.75rem;">
                      {{ payment.status }}
                    </span>
                  </td>
                  <td class="py-2">
                    <button @click="getRequest(payment)" data-toggle="modal" data-target="#slideRightModalAll"
                      class="btn btn-sm btn-outline-primary">
                      <i class="fe fe-eye mr-1"></i> View
                    </button>
                  </td>
                </tr>
                <tr v-if="!payments.data || !payments.data.length">
                  <td colspan="5" class="text-center py-5 text-muted">
                    <i class="fe fe-inbox" style="font-size:2rem;opacity:0.3;"></i>
                    <p class="mt-2 mb-0">No payments found</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="card-body pt-2">
            <b-pagination v-model="currentPage" :total-rows="payments.total" :per-page="form.paginate_value"
              aria-controls="my-table" @input="getPaymentAgency(currentPage ? currentPage : 1)"></b-pagination>
          </div>
        </div>

      </div>

      <!-- ── Transaction Details Modal ── -->
      <div class="modal fade modal-slide-right" id="slideRightModalAll" tabindex="-1" role="dialog"
        aria-labelledby="slideRightModalLabel" aria-hidden="true">
        <div class="modal-dialog scrollmodal" role="document">
          <div class="modal-content" id="printDiv" ref="printNow">
            <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f, #2563eb);">
              <div class="d-flex align-items-center">
                <div class="header-icon-wrap mr-3" style="width:36px;height:36px;">
                  <i class="fe fe-file-text" style="font-size:16px;color:#fff;"></i>
                </div>
                <div>
                  <h5 class="modal-title mb-0 text-white font-weight-bold" id="slideRightModalLabel">
                    Transaction Details
                  </h5>
                  <span :class="`badge badge-soft-${single_request.color} mt-1`" style="font-size:.72rem;">
                    {{ single_request.status }}
                  </span>
                </div>
              </div>
              <button type="button" class="close text-white ml-auto" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div class="modal-body p-0">
              <ul class="list-unstyled mb-0">
                <li class="info-row" v-if="single_request.client != null">
                  <i class="fe fe-user info-icon"></i>
                  <div>
                    <p class="info-label">Enrollee Name</p>
                    <a :href="`/client/${single_request.client.id}`" target="_blank">
                      <p class="info-value mb-0">{{ single_request.client.full_name }}
                        ({{ single_request.client.id_card_number }})</p>
                    </a>
                  </div>
                </li>
                <li class="info-row" v-if="single_request.client != null">
                  <i class="fe fe-briefcase info-icon"></i>
                  <div>
                    <p class="info-label">Programme</p>
                    <p class="info-value">{{ single_request.client.sector }}</p>
                  </div>
                </li>
                <li class="info-row" v-if="single_request.client != null">
                  <i class="fe fe-phone info-icon"></i>
                  <div>
                    <p class="info-label">Phone Number</p>
                    <p class="info-value">{{ single_request.client.phone_number }}</p>
                  </div>
                </li>
                <li class="info-row" v-if="single_request.client != null">
                  <i class="fe fe-mail info-icon"></i>
                  <div>
                    <p class="info-label">Email</p>
                    <p class="info-value">{{ single_request.client.email }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-dollar-sign info-icon"></i>
                  <div>
                    <p class="info-label">Amount</p>
                    <p class="info-value">&#8358;{{ single_request.amount | numeral(0, 0) }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-tag info-icon"></i>
                  <div>
                    <p class="info-label">Type</p>
                    <p class="info-value">{{ single_request.type }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-file-text info-icon"></i>
                  <div>
                    <p class="info-label">Description</p>
                    <p class="info-value">{{ single_request.description }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-hash info-icon"></i>
                  <div>
                    <p class="info-label">Transaction ID</p>
                    <p class="info-value">{{ paystack_transaction.id }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-key info-icon"></i>
                  <div>
                    <p class="info-label">Ref</p>
                    <p class="info-value">{{ single_request.transaction_ref }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-clock info-icon"></i>
                  <div>
                    <p class="info-label">Date Requested</p>
                    <p class="info-value">
                      {{ single_request.created_at | moment("dddd, MMMM Do YYYY, h:mm:ss a") }}
                    </p>
                  </div>
                </li>
                <li class="info-row" v-for="activity in single_request.activities.data" :key="activity.id">
                  <i class="fe fe-activity info-icon"></i>
                  <div>
                    <p class="info-label">{{ activity.description }} By</p>
                    <p class="info-value mb-0">
                      <span v-if="activity.causer != null">{{ activity.causer.full_name }}</span>
                      <span v-else>Online</span>
                    </p>
                  </div>
                </li>
              </ul>

              <div class="px-4 py-3 border-top">
                <a target="_blank" class="btn btn-sm btn-outline-secondary"
                  :href="`/payment/transaction/${single_request.id}`">
                  <i class="fe fe-external-link mr-1"></i> View Receipt
                </a>
              </div>

              <div class="px-4 pb-3">
                <button type="button" class="btn btn-sm btn-outline-info" @click="show = !show">
                  <i class="fe fe-plus mr-1"></i> Add Note
                </button>
                <div class="mt-3" v-show="show">
                  <label class="small font-weight-bold text-secondary">Leave a Comment</label>
                  <textarea rows="3" class="form-control mb-2" v-model="body"></textarea>
                  <button type="button" class="btn btn-submit px-4" @click="addComment">
                    <i class="fe fe-send mr-1"></i> Submit
                  </button>
                </div>
              </div>

              <div class="px-4 pb-4">
                <h6 class="font-weight-bold mb-3"><i class="fe fe-message-circle mr-1 text-primary"></i> Comments</h6>
                <div v-if="single_request.comments && single_request.comments.length">
                  <div class="media mb-3 p-3" style="background:#f8f9fa;border-left:3px solid #2563eb;border-radius:6px;"
                    v-for="comment in single_request.comments" :key="comment.id">
                    <div class="media-body">
                      <p class="mb-1">{{ comment.body }}</p>
                      <small class="text-muted" v-if="comment.user != null">
                        <strong>{{ comment.user.full_name }}</strong>
                      </small>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center text-muted py-4">
                  <i class="fe fe-message-circle" style="font-size:1.6rem;"></i>
                  <p class="mt-2 mb-0" style="font-size:.85rem;">No comments yet</p>
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <button v-if="single_request.status == 'created' || single_request.status == 'rejected'"
                class="btn btn-success" @click="updateRequestA(single_request, `approved`)">
                <i class="fe fe-check mr-1"></i> Approve
              </button>
              <button v-if="single_request.status == 'created' || single_request.status == 'approved'"
                class="btn btn-danger" @click="updateRequestA(single_request, `rejected`)">
                <i class="fe fe-x mr-1"></i> Decline
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
// Import component
import Loading from "vue-loading-overlay";
// Import stylesheet
import "vue-loading-overlay/dist/vue-loading.css";
import { chatContextMixin } from "@/chat/pageContext";
// Init plugin
let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: {
    Navbar,
    Loading,
  },
  mixins: [chatContextMixin],
  data() {
    return {
      agency: agency,
      statuses: ["created", "approved", "rejected"],
      types: ["online", "offline"],
      show: false,
      body: "",
      form: {
        from: "",
        to: "",
        type: "",
        status: "created",
        paginate_value: 30,
      },
      currentPage: 1,
      current_page: 1,
      rows: "1",
      isLoading: false,
      fullPage: true,
      paystack_transaction: "",
      json_fields: {
        Date: "created_at",
        "Enrollee Full Name": "client.full_name",
        "Enrollee ID": "client.id_card_number",
        "Type ": "type",
        "Status ": "status",
        Amount: "amount",
        Comments: {
          field: "comments",
          callback: (comments) => {
            if (!Array.isArray(comments) || !comments.length) {
              return "";
            }
            return comments.map((comment) => comment.body).filter(Boolean).join(" | ");
          },
        },
      },
      single_request: {
        client: "",
        status: "",
        activities: { data: [] },
      },
      user: null,
      employees: "",
      filterparams: "",
      providers: "",
      provider_id: "",
      state: "",
      payments: "",
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  methods: {
    getPaymentAgency(currentPage) {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/transactions`, {
          params: {
             page: currentPage,
            agency_id: agency.id,
            id_card_number: this.form.id_card_number,
            provider_id: this.form.provider_id,
            type: this.form.type,
            status: this.form.status,
            from: this.form.from,
            to: this.form.to,
            paginate_value: this.form.paginate_value,
          },
        })
        .then((response) => {
          this.payments = response.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getRequest(trf) {
      this.isLoading = true;

      this.single_request = trf;
      this.setChatContext("finance", trf.id, "#slideRightModalAll");
      if (trf.type == "online") this.getPaystackTrx(trf);

      this.isLoading = false;
    },

    getPaystackTrx(trf) {
      this.axios
        .get(`/api/v1/auth/transaction/online/${trf.transaction_ref}`)
        .then((response) => {
          this.paystack_transaction = response.data.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    addCapitation() {
      if (confirm("Are you sure you want to Submit")) {
        this.axios
          .post(`/api/v1/auth/addcaptitation`, {
            agency_id: 90,
            provider_id: this.provider_id,
            type: "capitations",
          })
          .then((response) => {
            console.log(response);
            this.$toasted.info("Capitation Created Successfully!", {
              position: "top-center",
              duration: 3000,
            });
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },

    addComment() {
      this.axios
        .post(`/api/v1/auth/addComment/referral`, {
          user_id: this.user.id,
          transaction_id: this.single_request.id,
          body: this.body,
        })
        .then((response) => {
          console.log(response);
          this.show = false;
          this.$toasted.info("added Successfully!", {
            position: "top-center",
            duration: 3000,
          });
        });
    },

    updateRequestA(trf, status) {
      if (confirm("Are you sure you want to update this request")) {
        this.isLoading = true;
        this.axios
          .put(`/api/v1/auth/transactions/${trf.id}`, {
            status: status,
          })
          .then((response) => {
            console.log(response);
            this.getPaymentAgency();
            this.$toasted.success("Updated Successfully", {
              position: "top-center",
              duration: 3000,
            });
            this.isLoading = false;
          })
          .catch((error) => {
            console.log(error.response);
            this.isLoading = false;
          });
      }
    },
  },
  created() {
    this.getPaymentAgency();
  },
};
</script>

<style scoped>
.scrollmodal {
  height: 100%;
  margin: 0;
}

.scrollmodal .modal-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.scrollmodal .modal-body {
  overflow-y: auto;
  flex: 1;
}
</style>
